import { useEffect, useState } from 'react';

export interface Countdown {
  hours: number;
  minutes: number;
  /** True once the target has passed. Caller decides what to show
   *  (typically a different CTA or a "0h 0m" frozen state). */
  expired: boolean;
}

/**
 * Live `h/m` countdown to an ISO timestamp. Updates on a 30 s tick
 * — granular enough for a minute-precision display to never lag
 * by more than ~30 s, cheap enough to stay invisible in the
 * profiler. Stops ticking once the target passes; the consumer
 * gets `expired: true` and decides the UI from there.
 */
export function useCountdown(target: string | null): Countdown {
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!target) return;
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [target]);

  if (!target) return { hours: 0, minutes: 0, expired: true };
  const ms = new Date(target).getTime() - now;
  if (!Number.isFinite(ms) || ms <= 0) {
    return { hours: 0, minutes: 0, expired: true };
  }
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  return { hours, minutes, expired: false };
}
